import { computed, onScopeDispose, readonly, ref, shallowRef, watch } from 'vue';
import type { ISchoolGradeInputProps } from './schoolGradeInput.types';

/** Owns the current grade. Drafts stay in the editor; accepted writes finish after unmount. */
export const useSchoolGradeValue = (
  props: Pick<ISchoolGradeInputProps, 'modelValue' | 'save'>,
  emit: (value: number | null) => void,
) => {
  const selection = shallowRef<{ value: number | null }>();
  const value = computed(() => (selection.value ? selection.value.value : props.modelValue));
  const isSaving = ref(false);
  const failedSave = ref(false);
  let activeSelection: typeof selection.value;
  let acknowledgedSelection: typeof selection.value;
  let saveQueue = Promise.resolve();
  let disposed = false;
  const save = (next: number | null): void => {
    if (next === value.value && !failedSave.value) return;
    const request = { value: next };
    const saveValue = props.save;
    selection.value = request;
    failedSave.value = false;
    if (!saveValue) {
      emit(next);
      return;
    }
    isSaving.value = true;
    saveQueue = saveQueue.then(async () => {
      activeSelection = request;
      try {
        await saveValue(next);
      } catch {
        if (!disposed && selection.value === request) failedSave.value = true;
      } finally {
        activeSelection = undefined;
        if (!disposed && selection.value === request) {
          isSaving.value = false;
          if (!failedSave.value && acknowledgedSelection === request) selection.value = undefined;
        }
      }
    });
  };
  // Only a matching value acknowledges the active write; late refreshes may belong to an older save.
  watch(
    () => props.modelValue,
    (next) => {
      acknowledgedSelection = activeSelection?.value === next ? activeSelection : undefined;
      if (!isSaving.value && !failedSave.value) selection.value = undefined;
    },
    { flush: 'sync' },
  );
  onScopeDispose(() => {
    disposed = true;
  });
  return { value, isSaving: readonly(isSaving), failedSave: readonly(failedSave), save };
};
